import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import EnhancedDashboard from './pages/EnhancedDashboard';

import LiveTracking from './pages/LiveTracking';
import TicketTracking from './pages/TicketTracking';
import Logistics from './pages/Logistics';
import EventStatus from './pages/EventStatus';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import Profile from './pages/Profile';
import EventPage from './pages/Event';
import TTXImport from './pages/TTXImport';
import Playback from './pages/Playback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Attendance from './pages/Attendance';
import EmergencyAlerts from './pages/EmergencyAlerts';
import HOTOTimingsReport from './pages/HOTOTimingsReport';
import OSPanel from './pages/OSPanel';
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from './lib/supabaseClient';
import { LocationSharingProvider } from './context/LocationSharingContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UserRoleProvider } from './contexts/UserRoleContext';
import { AlertsProvider } from './contexts/AlertsContext';
import AuthLayout from './components/AuthLayout';
import { useTranslation } from 'react-i18next';


function PrivateRoute({ children }: { children: ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return null; // Or a loading spinner
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function GodRoute({ children }: { children: ReactElement }) {
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('');
  const { user } = useAuth(); // Use context to get user

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        const { data } = await supabase
          .from('users')
          .select('user_role')
          .eq('id', user.id)
          .single();
        if (data) setUserRole(data.user_role);
      }
      setLoading(false);
    };

    if (user) {
      fetchUserRole();
    } else {
      setLoading(false);
    }
  }, [user]);

  if (loading) return null;
  if (userRole !== 'god') return <Navigate to="/" replace />;
  return children;
}

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<AuthLayout><Login /></AuthLayout>} />
      <Route path="/signup" element={<AuthLayout><SignUp /></AuthLayout>} />
      <Route path="/forgot-password" element={<AuthLayout><ForgotPassword /></AuthLayout>} />
      <Route path="/reset-password" element={<AuthLayout><ResetPassword /></AuthLayout>} />

      {/* Private routes */}
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <UserRoleProvider>
              <AlertsProvider>
                <Layout>
                  <Routes>
                    <Route path="/" element={<EnhancedDashboard />} />
                    <Route path="/dashboard" element={<EnhancedDashboard />} />
                    <Route path="/live-tracking" element={<LiveTracking />} />
                    <Route path="/ticket-tracking" element={<TicketTracking />} />
                    <Route path="/logistics" element={<Logistics />} />
                    <Route path="/event-status" element={<EventStatus />} />
                    <Route path="/attendance" element={<Attendance />} />
                    <Route path="/event" element={<EventPage />} />
                    <Route path="/ttx-import" element={<TTXImport />} />
                    <Route path="/playback" element={<Playback />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/emergency-alerts" element={<EmergencyAlerts />} />
                    <Route path="/hoto-report" element={<HOTOTimingsReport />} />
                    <Route
                      path="/ospanel"
                      element={
                        <GodRoute>
                          <OSPanel />
                        </GodRoute>
                      }
                    />
                    <Route
                      path="/playback"
                      element={
                        <GodRoute>
                          <Playback />
                        </GodRoute>
                      }
                    />
                    {/* Add a catch-all for any other private routes to redirect to dashboard */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </AlertsProvider>
            </UserRoleProvider>
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Check localStorage for device language
    const localLang = localStorage.getItem('language');
    if (localLang) {
      i18n.changeLanguage(localLang);
    }
  }, []);

  return (
    <Router>
      <LocationSharingProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </LocationSharingProvider>
    </Router>
  );
}

export default App;
